import { normalizeDate } from '../../../utils'
import { IShipmentFormInput } from '../schema'

export interface ValidationError {
  id: string
  message: string
  severity: 'error' | 'warning'
  step?: number
  dataGroup?: string
}

export const validateTemperatureControlAlignment = (
  data: IShipmentFormInput
): ValidationError[] => {
  const errors: ValidationError[] = []

  if (!data.bookings) return errors

  data.bookings.forEach((booking, bookingIndex) => {
    if (!booking.packaging) return

    booking.packaging.forEach((packaging, packagingIndex) => {
      const isLoadTemperatureControlled = packaging.temperatureControlled
      const loadTemperatureRange = packaging.temperatureRange

      if (packaging.products && Array.isArray(packaging.products)) {
        let hasTemperatureControlledProduct = packaging.products.length === 0

        packaging.products.forEach((product, productIndex) => {
          const isProductTemperatureControlled = product.temperatureControlled
          const productTemperatureRange = product.temperatureSetting?.temperatureRangeId

          if (isProductTemperatureControlled) {
            hasTemperatureControlledProduct = true
          }

          if (!isLoadTemperatureControlled && isProductTemperatureControlled) {
            errors.push({
              id: `temp-mismatch-${bookingIndex}-${packagingIndex}-${productIndex}`,
              message: `Consignment #${bookingIndex + 1}, Load #${
                packagingIndex + 1
              }: Non-temperature controlled load contains temperature controlled content. Please either enable temperature control for the load or remove temperature control from the content.`,
              severity: 'error',
              step: 3,
              dataGroup: 'Load Contents',
            })
          }

          if (
            isLoadTemperatureControlled &&
            isProductTemperatureControlled &&
            loadTemperatureRange &&
            productTemperatureRange &&
            loadTemperatureRange !== productTemperatureRange
          ) {
            errors.push({
              id: `temp-range-mismatch-${bookingIndex}-${packagingIndex}-${productIndex}`,
              message: `Consignment #${bookingIndex + 1}, Load #${
                packagingIndex + 1
              }: Temperature range mismatch. Load temperature range does not match content temperature range.`,
              severity: 'error',
              step: 3,
              dataGroup: 'Load Contents',
            })
          }
        })

        if (isLoadTemperatureControlled && !hasTemperatureControlledProduct) {
          errors.push({
            id: `temp-missing-${bookingIndex}-${packagingIndex}`,
            message: `Consignment #${bookingIndex + 1}, Load #${
              packagingIndex + 1
            }: Temperature controlled load must contain at least one temperature controlled content item.`,
            severity: 'error',
            step: 3,
            dataGroup: 'Load Contents',
          })
        }
      }
    })
  })

  return errors
}

export const validateScheduledDates = (
  data: IShipmentFormInput
): ValidationError[] => {
  const errors: ValidationError[] = []
  const pastDateErrorsByBooking = new Map<number, boolean>()

  if (!data.bookings) return errors

  const now = normalizeDate(new Date())

  data.bookings.forEach((booking: any, bookingIndex: number) => {
    const shipFrom = booking.shipFrom
    const deliverTo = booking.deliverTo
    const waypoints = booking.waypoints

    const checkPastDate = (date: any) => {
      if (!date) return false
      return normalizeDate(date) < now
    }

    if (!shipFrom?.loadingDate) {
      errors.push({
        id: `schema-date-error-${bookingIndex}-ship-from-required`,
        message: `Consignment #${bookingIndex + 1}: Ship From Scheduled Date is required.`,
        severity: 'error',
        step: 1,
        dataGroup: 'Shipment Information',
      })
    }

    let hasPastDate = false

    if (checkPastDate(shipFrom?.loadingDate)) hasPastDate = true
    if (checkPastDate(deliverTo?.deliveryDate)) hasPastDate = true
    if (checkPastDate(shipFrom?.transitDetails?.scheduledDate)) hasPastDate = true
    if (checkPastDate(deliverTo?.transitDetails?.scheduledDate)) hasPastDate = true
    if (booking.emptyContainerReturn?.scheduledDate &&
        checkPastDate(booking.emptyContainerReturn.scheduledDate)) hasPastDate = true

    if (waypoints) {
      waypoints.forEach((waypoint: any) => {
        if (checkPastDate(waypoint?.transitDetails?.scheduledDate)) hasPastDate = true
      })
    }

    if (hasPastDate && !pastDateErrorsByBooking.has(bookingIndex)) {
      pastDateErrorsByBooking.set(bookingIndex, true)
      errors.push({
        id: `schema-date-error-${bookingIndex}-past-date`,
        message: `Consignment #${bookingIndex + 1}: Scheduled date cannot be in the past.`,
        severity: 'error',
        step: 1,
        dataGroup: 'Shipment Information',
      })
    }
  })

  return errors
}

export const validateShipmentData = (
  data: IShipmentFormInput
): ValidationError[] => {
  const temperatureErrors = validateTemperatureControlAlignment(data)
  const dateErrors = validateScheduledDates(data)

  return [...temperatureErrors, ...dateErrors]
}
